import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useNotification} from "../../utils/hooks/useNotification";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {deleteFetcher, getFetcher, putFetcher} from "../../utils/api/fetchers";
import {Category} from "../../schemas/category/categoriesSchema";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import useSWR from "swr";
import Loading from "../../components/status/Loading";
import ErrorMessage from "../../components/status/ErrorMessage";
import {useAuth} from "../../utils/hooks/useAuth";
import UserRole from "../../schemas/user/roles";
import {Club} from "../../schemas/club/clubSchema";
import {Boat, boatSchema} from "../../schemas/boats/boatsSchema";
import {ColorInput} from "@mantine/core";
import BadgeIcon from '@mui/icons-material/Badge';
import {UserAccount} from "../../schemas/user/userAccountSchemas";
import InputAdornment from "@mui/material/InputAdornment";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useConfirm} from "material-ui-confirm";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const BoatDetails: React.FC = () => {
    const {hasPermission} = useAuth();
    const confirm = useConfirm();
    const navigate = useNavigate();
    const {showNotification} = useNotification();
    const {data: categories} = useSWR<Category[]>('/categories', getFetcher);
    const {data: clubs} = useSWR<Club[]>('/clubs/all', getFetcher);
    const {boatId} = useParams<{ boatId: string }>();
    const {data: boat, isLoading, error, mutate} = useSWR<Boat>(`/boats/${boatId}`, getFetcher);
    const {data: registrar} = useSWR<UserAccount>(`/users/${boat?.registrarId}`, getFetcher);
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        watch,
        formState: {errors, isDirty},
    } = useForm<Boat>({
        defaultValues: boat,
        resolver: zodResolver(boatSchema),
    });
    const isMobile = useMediaQuery('(max-width: 600px)');

    const [editMode, setEditMode] = useState<boolean>(false);
    const readOnly = !hasPermission(UserRole.ORGANIZER);
    const noEdit = readOnly || !editMode;
    const deleteMarkOfficialPermission = hasPermission(UserRole.COMMODORE);

    const [colorValue, setColorValue] = useState<string | undefined>(undefined);
    const [colorNote, setColorNote] = useState<string | undefined>(undefined);
    const [boatLengthText, setBoatLengthText] = useState<string>("");

    useEffect(() => {
        if (boat) {
            reset(boat);

            const [color, note] = (boat.color ?? "-").split("-");
            setColorValue(color);
            setColorNote(note);
        }
    }, [boat, reset]);

    const boatLengthValue = watch('length');
    useEffect(() => {
        if (boatLengthValue != null && !isNaN(boatLengthValue)) {
            setBoatLengthText(boatLengthValue.toFixed(2).replace(/\.?0+$/, '')); // 5.00 → 5
        } else {
            setBoatLengthText('');
        }
    }, [boatLengthValue]);

    const handleBlurLength = () => {
        const normalized = boatLengthText.replace(',', '.');
        const parsed = parseFloat(normalized);
        if (!isNaN(parsed)) {
            setValue('length', parsed, { shouldDirty: true });
        } else {
            setValue('length', undefined, { shouldDirty: true });
        }
    };

    const handleColorChange = (color: string) => {
        setColorValue(color);
        setValue('color', `${color}-${colorNote}`, {shouldDirty: true});
    };

    const handleNoteChange = (colorText: string) => {
        setColorNote(colorText);
        setValue('color', `${colorValue}-${colorText}`, {shouldDirty: true});
    };

    const resetColor = () => {
        if (boat) {
            const [color, note] = (boat.color ?? "-").split("-");
            setColorValue(color);
            setColorNote(note);
        }
    }

    const onSubmit = async (data: Boat) => {
        if (!isDirty) {
            showNotification("Nebyly provedeny žádné změny", "info");
            setEditMode(false);
            return;
        }

        putFetcher(`/boats/${boatId}`, {
            ...data,
            categoryId: data.category.id,
            clubId: data.club?.id,
        })
            .then(() => {
                showNotification("Loď byla úspěšně upravena", "success");
                setEditMode(false);
                mutate();
            })
            .catch((error) => {
                if (error.response?.status === 409) {
                    showNotification("Loď s tímto startovním číslem již existuje", "error");
                    return;
                }
                showNotification("Loď se nepodařilo aktualizovat", "error");
            })
    }

    const handleDelete = (boatId: number) => {
        confirm({
            description: `Opravdu chcete smazat tuto loď?`,
            title: "Smazat loď",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                deleteFetcher(`/boats/${boatId}`).then(() => {
                    showNotification('Loď byla smazána', 'success');
                    navigate('/boats');
                }).catch(() => {
                    showNotification('Loď se nepodařilo smazat', 'error');
                });
            }).catch(() => {
        });
    }

    const handleMarkOfficial = (boatId: number) => {
        confirm({
            description: <>
                <Typography>Opravdu chcete loď schválit?</Typography>
                <Typography>Pro schválení lodi je zapotřebí role komodora.</Typography>
            </>,
            title: "Schválit loď",
            confirmationText: "Schválit",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "success"},
        })
            .then(() => {
                putFetcher(`/boats/${boatId}/register`, {}).then(() => {
                    showNotification('Loď byla schválena', 'success');
                    mutate();
                }).catch((err) => {
                    if (err.response?.status === 403) {
                        showNotification('Nemáte oprávnění schválit loď', 'error');
                        return;
                    }
                    showNotification('Loď se nepodařilo schválit', 'error');
                });
            }).catch(() => {
        });
    }

    if (isLoading || !boat || !categories || !clubs) return <Loading/>;
    if (error) return <ErrorMessage/>

    return (
        <Paper elevation={3} sx={{padding: 4, maxWidth: 600, margin: '0 auto'}}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between">
                <Typography variant="h4" gutterBottom>
                    Detail lodi
                </Typography>
                {deleteMarkOfficialPermission && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: isMobile ? 2 : 0}}>
                        <Chip
                            color={boat.officialRegistered ? "success" : "error"}
                            label={boat.officialRegistered ? "Schválená" : "Neschválená"}
                            sx={{px: 1}}
                        />
                        {!boat.officialRegistered && (
                            <>
                                <ArrowForwardIcon/>
                                <Button
                                    variant="outlined"
                                    color="success"
                                    onClick={() => handleMarkOfficial(boat.id)}
                                >
                                    Schválit
                                </Button>
                            </>
                        )}
                    </Stack>
                )}
            </Stack>
            <Box component="form" noValidate>
                <Stack spacing={3}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Jméno"
                        variant="standard"
                        required
                        {...register('name')}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        InputProps={{readOnly: noEdit}}
                    />
                    <Autocomplete
                        value={watch('category') ?? categories.find((c) => c.id === boat.category.id)}
                        options={categories}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(event, value) => {
                            if (value) setValue('category', value, {shouldDirty: true});
                        }}
                        readOnly={noEdit}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Kategorie"
                                variant="standard"
                                required
                                error={!!errors.category}
                                helperText={errors.category?.message}
                            />
                        )}
                    />
                    <Autocomplete
                        value={watch('club') ?? null}
                        options={clubs}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(event, value) => {
                            setValue('club', value ?? undefined, {shouldDirty: true});
                        }}
                        readOnly={noEdit}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Klub"
                                variant="standard"
                                error={!!errors.club}
                                helperText={errors.club?.message}
                            />
                        )}
                    />
                    <Stack direction={isMobile ? "column" : "row"} spacing={1}>
                        <TextField
                            fullWidth
                            label="Evidenční číslo"
                            variant="standard"
                            {...register('boatNumber')}
                            error={!!errors.boatNumber}
                            helperText={errors.boatNumber?.message}
                            InputProps={{readOnly: noEdit}}
                        />
                        <TextField
                            fullWidth
                            label="Číslo na plachtě"
                            variant="standard"
                            {...register('sailNumber')}
                            error={!!errors.sailNumber}
                            helperText={errors.sailNumber?.message}
                            InputProps={{readOnly: noEdit}}
                        />
                        <TextField
                            fullWidth
                            label="Závodní číslo (trvalé)"
                            variant="standard"
                            type="number"
                            {...register('raceNumber', {
                                setValueAs: (value) => isNaN(parseInt(value)) ? undefined : parseInt(value),
                            })}
                            error={!!errors.raceNumber}
                            helperText={errors.raceNumber?.message}
                            InputProps={{
                                readOnly: noEdit,
                            }}
                            inputProps={{min: 0}}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Typ lodi"
                            variant="standard"
                            {...register('type')}
                            error={!!errors.type}
                            helperText={errors.type?.message}
                            InputProps={{readOnly: noEdit}}
                        />
                        <TextField
                            fullWidth
                            label="Délka lodi"
                            variant="standard"
                            value={boatLengthText}
                            onChange={(e) => setBoatLengthText(e.target.value)}
                            onBlur={handleBlurLength}
                            error={!!errors.length}
                            helperText={errors.length?.message}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Typography variant="subtitle1">m</Typography>
                                    </InputAdornment>
                                ),
                                readOnly: noEdit,
                            }}
                            inputProps={{
                                inputMode: "decimal",
                                pattern: "[0-9]+([.,][0-9]+)?",
                                onKeyDown: (e) => {
                                    const allowedKeys = [
                                        'Backspace',
                                        'Tab',
                                        'Delete',
                                        'ArrowLeft',
                                        'ArrowRight',
                                        'Home',
                                        'End',
                                        'Enter',
                                    ];
                                    const isNumber = /^[0-9.,]$/.test(e.key);
                                    if (!isNumber && !allowedKeys.includes(e.key)) {
                                        e.preventDefault();
                                    }
                                },
                            }}
                        />
                    </Stack>
                    <Stack direction="row" alignItems="baseline" spacing={2}>
                        <TextField
                            label="Barva"
                            variant="standard"
                            fullWidth
                            value={colorNote}
                            onChange={(event) => handleNoteChange(event.target.value)}
                            error={!!errors.color}
                            helperText={errors.color?.message}
                            InputProps={{readOnly: noEdit}}
                        />
                        <ColorInput
                            title="Vyberte barvu"
                            value={colorValue}
                            onChange={(event) => handleColorChange(event)}
                            error={!!errors.color}
                            swatchesPerRow={5}
                            swatches={[
                                '#000000',
                                '#FFFFFF',
                                '#FF0000',
                                '#008000',
                                '#0000FF',
                                '#ffa915',
                                '#FFFF00',
                                '#808080',
                                '#00008B',
                                '#800020',
                            ]}
                            readOnly={noEdit}
                        />
                    </Stack>
                    {!readOnly && (
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="subtitle1">Vytvořil:</Typography>
                            {registrar ? (<>
                                    <Typography>{registrar.firstName} {registrar.lastName}</Typography>
                                    <IconButton
                                        title="Zobrazit profil"
                                        sx={{pb:1.5}}
                                        onClick={() => navigate(`/users/${registrar.id}`)}
                                    >
                                        <BadgeIcon/>
                                    </IconButton>
                                </>
                            ) : (
                                <Skeleton width={200}/>
                            )}
                        </Stack>
                    )}
                    {!readOnly && (
                        editMode ? (
                            <Stack direction='row' spacing={2}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    sx={{width: 250}}
                                    onClick={() => {
                                        setEditMode(false);
                                        resetColor();
                                        reset(boat);
                                    }}
                                >
                                    Zrušit změny
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Uložit
                                </Button>
                            </Stack>
                        ) : (
                            <Stack direction='row' spacing={2}>
                                {hasPermission(UserRole.ADMIN) && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        sx={{px: 10}}
                                        onClick={() => handleDelete(boat.id)}
                                    >
                                        Smazat
                                    </Button>
                                )}
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => setEditMode(true)}
                                >
                                    Upravit
                                </Button>
                            </Stack>
                        )
                    )}
                </Stack>
            </Box>
        </Paper>
    );
};

export default BoatDetails;
